import { useGlowUsage } from '../../hooks/useGlowUsage'
import { Card } from '../../components/ui/Card'
import { Meter } from '../../components/ui/Meter'
import { Spinner } from '../../components/ui/Spinner'
import { ErrorBanner } from '../../components/ErrorBanner'
import { formatBytes, formatCount, percent } from '../../lib/format'
import type { Glow, GlowUsage } from '../../api/types'

// UsageTab — read-only, point-in-time per-glow usage dashboard. Two cards:
//   1. Storage — a per-glow segmented breakdown bar (Bundles / Shared /
//      Per-user / Capture / Records) sized against this glow's own footprint,
//      plus a slim account-pool meter so the shared cap fill is always visible.
//   2. Bandwidth & requests — two gradient meters driven by the **account**
//      fill (the quota the data plane actually enforces — going over throttles
//      every glow), each annotated with this glow's own number.
// Single data source: GET /v1/glows/{id}/usage via useGlowUsage. The cap fields
// are account-wide and the `account_*` counters drive the meters; this glow's
// own numbers are shown as secondary figures. A zero cap renders as
// "Unlimited". When the `account_*` fields are absent (an older console-api),
// the meters degrade to this glow's own figure against the cap.
export function UsageTab({ glow }: { glow: Glow }) {
  const { data, isLoading, error } = useGlowUsage(glow.id)

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (error || !data) {
    return (
      <ErrorBanner>
        {error ? (error as Error).message : 'Usage unavailable.'}
      </ErrorBanner>
    )
  }

  return (
    <div className="max-w-[560px] space-y-4">
      <StorageCard storage={data.storage} />
      <BandwidthCard bandwidth={data.bandwidth} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Storage card
// ---------------------------------------------------------------------------

// The five segment colors aren't in tailwind.config.js, so keep the raw hex
// here as the single local source of truth (bar + breakdown swatches share it).
const GROUP_COLORS = {
  bundles: '#0d9488', // teal
  shared: '#f97316', // orange
  users: '#2563eb', // blue
  private: '#7c3aed', // violet
  records: '#db2777', // pink
} as const

type GroupKey = keyof typeof GROUP_COLORS

const GROUP_LABELS: Record<GroupKey, string> = {
  bundles: 'Bundles',
  shared: 'Shared',
  users: 'Per-user',
  // The group key stays `private` (server-driven, from the GCS prefix); only
  // the display label tracks the `capture` API rename.
  private: 'Capture',
  records: 'Records',
}

const GROUP_DESCRIPTIONS: Record<GroupKey, string> = {
  bundles: 'Version assets across every committed deploy',
  shared: 'window.backlit.data — glow-shared store',
  users: 'window.backlit.userdata — per-user silos',
  private: 'window.backlit.capture — write-only capture store',
  records: 'window.backlit.records — public-read, owner-write store',
}

// Fixed segment / breakdown order.
const GROUP_ORDER: GroupKey[] = [
  'bundles',
  'shared',
  'users',
  'private',
  'records',
]

function StorageCard({ storage }: { storage: GlowUsage['storage'] }) {
  const cap = storage.cap_bytes
  const total = storage.total_bytes
  const breakdown = storage.breakdown_available

  // The cap is account-wide, so the meter that matters is the account fill, not
  // this glow's slice. Guard the optional `account_total_bytes` with
  // `=== undefined` (never `|| fallback` — 0 is a valid count): when present it
  // drives the slim pool meter; when absent we degrade to this glow's own
  // total so the cap is still legible.
  const accountTotal =
    storage.account_total_bytes === undefined
      ? total
      : storage.account_total_bytes
  const haveAccount = storage.account_total_bytes !== undefined

  // Data subtotal (everything except Bundles).
  const dataSubtotal =
    storage.groups.shared +
    storage.groups.users +
    storage.groups.private +
    storage.groups.records

  return (
    <Card>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-medium text-cream">Storage</h2>
          <p className="mt-0.5 text-sm text-muted">
            Combined across Bundles &amp; Data
          </p>
        </div>
        <div className="sm:text-right">
          <div className="font-mono text-xl tabular-nums text-cream">
            {formatBytes(total)}
            <span className="text-muted"> this glow</span>
          </div>
        </div>
      </div>

      {/* Account-pool meter — the shared cap is what enforces, so surface its
          fill explicitly. Driven by the account total (or this glow's total as
          a fallback when the account_* field is absent). */}
      <div className="mt-4">
        <Meter
          label="Account storage pool"
          used={accountTotal}
          cap={cap}
          format={(n) => formatBytes(n)}
          secondary={
            cap > 0
              ? haveAccount
                ? `shared account cap — total across your glows: ${formatBytes(
                    accountTotal,
                  )} / ${formatBytes(cap)}`
                : `shared account cap: ${formatBytes(cap)}`
              : // cap === 0 only on a genuinely-unlimited (future paid) cap or a
                // stale backend — free owners now get concrete Spark caps. Keep
                // this cap-agnostic so it never misleadingly says "unlimited".
                'shared account cap'
          }
        />
      </div>

      {!breakdown ? (
        <p className="mt-4 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-muted">
          Live breakdown unavailable. Showing the cap only.
        </p>
      ) : (
        <>
          {/* Per-glow breakdown header */}
          <div className="mt-5 flex items-baseline justify-between">
            <span className="text-sm text-cream/80">This glow&apos;s storage</span>
            <span className="font-mono text-sm tabular-nums text-cream">
              {formatBytes(total)}
            </span>
          </div>

          {/* Segmented bar — sized against THIS glow's own footprint (so the
              segments stay legible for a small glow inside a large account
              pool), not the account cap. */}
          <div className="mt-1.5 flex h-3.5 w-full overflow-hidden rounded-full bg-surface-2">
            {GROUP_ORDER.map((key) => {
              const size = storage.groups[key]
              const widthPct = segmentWidth(size, total)
              if (widthPct <= 0) return null
              return (
                <div
                  key={key}
                  className="h-full border-r border-line last:border-r-0"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: GROUP_COLORS[key],
                  }}
                  title={`${GROUP_LABELS[key]}: ${formatBytes(size)}`}
                />
              )
            })}
          </div>

          {/* Ruler */}
          <div className="mt-1 flex justify-between font-mono text-[11px] tabular-nums text-muted">
            <span>0</span>
            <span>{total > 0 ? formatBytes(total / 2) : '—'}</span>
            <span>{total > 0 ? `${formatBytes(total)} total` : 'empty'}</span>
          </div>

          {storage.truncated && (
            <p className="mt-2 text-xs text-glow-500">
              Approximate — the glow has more objects than the live scan
              counts, so this figure is a partial sum.
            </p>
          )}

          {/* Breakdown list */}
          <div className="mt-5 space-y-2">
            <BucketRow
              color={GROUP_COLORS.bundles}
              label={GROUP_LABELS.bundles}
              description={GROUP_DESCRIPTIONS.bundles}
              size={storage.groups.bundles}
              pctOfUsed={rowPct(storage.groups.bundles, total)}
            />

            {/* DATA group header + subtotal */}
            <div className="flex items-center justify-between pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
              <span>Data</span>
              <span className="font-mono tabular-nums text-muted">
                {formatBytes(dataSubtotal)}
              </span>
            </div>

            <div className="space-y-2 border-l border-line pl-3">
              <BucketRow
                color={GROUP_COLORS.shared}
                label={GROUP_LABELS.shared}
                description={GROUP_DESCRIPTIONS.shared}
                size={storage.groups.shared}
                pctOfUsed={rowPct(storage.groups.shared, total)}
              />
              <BucketRow
                color={GROUP_COLORS.users}
                label={GROUP_LABELS.users}
                description={GROUP_DESCRIPTIONS.users}
                size={storage.groups.users}
                pctOfUsed={rowPct(storage.groups.users, total)}
              />
              <BucketRow
                color={GROUP_COLORS.private}
                label={GROUP_LABELS.private}
                description={GROUP_DESCRIPTIONS.private}
                size={storage.groups.private}
                pctOfUsed={rowPct(storage.groups.private, total)}
              />
              <BucketRow
                color={GROUP_COLORS.records}
                label={GROUP_LABELS.records}
                description={GROUP_DESCRIPTIONS.records}
                size={storage.groups.records}
                pctOfUsed={rowPct(storage.groups.records, total)}
              />
            </div>
          </div>
        </>
      )}
    </Card>
  )
}

// segmentWidth clamps a group's share of the cap to [0,100]%. A zero cap
// (Unlimited) yields 0 so the bar is empty rather than dividing by zero.
function segmentWidth(size: number, cap: number): number {
  if (cap <= 0) return 0
  const pct = (size / cap) * 100
  if (pct < 0) return 0
  if (pct > 100) return 100
  return pct
}

// rowPct is the breakdown row's "% of used total" (of total_bytes, not cap).
// Returns null when the used total is zero, which the row renders as blank.
function rowPct(size: number, total: number): number | null {
  return percent(size, total)
}

function BucketRow({
  color,
  hollow = false,
  label,
  description,
  size,
  pctOfUsed,
}: {
  color?: string
  hollow?: boolean
  label: string
  description?: string
  size: number
  pctOfUsed?: number | null
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2">
        <span
          className={`mt-0.5 h-3 w-3 shrink-0 rounded-sm ${
            hollow ? 'border border-line-strong bg-surface' : ''
          }`}
          style={hollow ? undefined : { backgroundColor: color }}
        />
        <div className="min-w-0">
          <div className="text-sm text-cream">{label}</div>
          {description && (
            <div className="break-words text-xs text-muted">{description}</div>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="font-mono text-sm tabular-nums text-cream">
          {formatBytes(size)}
        </div>
        {pctOfUsed != null && (
          <div className="text-xs text-muted">
            {pctOfUsed.toFixed(0)}% of used
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bandwidth card
// ---------------------------------------------------------------------------

function BandwidthCard({ bandwidth }: { bandwidth: GlowUsage['bandwidth'] }) {
  const { period, resetsInDays } = monthlyPeriod()

  // The caps are account-wide, so the meter is driven by the **account** fill
  // (the figure that decides whether every glow gets 429'd — risk X5). Guard
  // the optional `account_*` counters with `=== undefined` (never `|| fallback`
  // — 0 is a valid count): when present they drive the bar; when absent we
  // degrade to this glow's own counter against the account cap.
  const egressAccount =
    bandwidth.account_egress_bytes_used === undefined
      ? bandwidth.egress_bytes_used
      : bandwidth.account_egress_bytes_used
  const haveEgressAccount = bandwidth.account_egress_bytes_used !== undefined

  const requestsAccount =
    bandwidth.account_requests_count === undefined
      ? bandwidth.requests_count
      : bandwidth.account_requests_count
  const haveRequestsAccount = bandwidth.account_requests_count !== undefined

  return (
    <Card>
      <div>
        <h2 className="text-base font-medium text-cream">
          Bandwidth &amp; requests
        </h2>
        <p className="mt-0.5 text-sm text-muted">
          {period} · resets in {resetsInDays}{' '}
          {resetsInDays === 1 ? 'day' : 'days'}
        </p>
      </div>

      <div className="mt-4 space-y-5">
        <Meter
          label="Bandwidth"
          used={egressAccount}
          cap={bandwidth.egress_cap_bytes}
          format={(n) => formatBytes(n)}
          secondary={
            <>
              this glow: {formatBytes(bandwidth.egress_bytes_used)}
              {haveEgressAccount && bandwidth.egress_cap_bytes > 0 && (
                <>
                  {' · '}shared account cap — total across your glows:{' '}
                  {formatBytes(egressAccount)} /{' '}
                  {formatBytes(bandwidth.egress_cap_bytes)}
                </>
              )}
            </>
          }
        />
        <Meter
          label="Requests"
          used={requestsAccount}
          cap={bandwidth.requests_cap}
          format={formatCount}
          secondary={
            <>
              this glow: {formatCount(bandwidth.requests_count)}
              {haveRequestsAccount && bandwidth.requests_cap > 0 && (
                <>
                  {' · '}shared account cap — total across your glows:{' '}
                  {formatCount(requestsAccount)} /{' '}
                  {formatCount(bandwidth.requests_cap)}
                </>
              )}
            </>
          }
        />
      </div>

      <p className="mt-4 text-[11px] text-muted">
        updates every few seconds · resets monthly · account-wide caps
      </p>
    </Card>
  )
}

// monthlyPeriod derives the display period (current UTC month) and the number
// of days until the next reset. Counters reset on the 1st of the month at
// 00:00 UTC (CLAUDE.md §12 cmd/usage-reset), so resets-in is the whole-day
// count from now to the first instant of next month.
function monthlyPeriod(): { period: string; resetsInDays: number } {
  const now = new Date()
  const period = now.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
  const nextReset = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  const msUntil = nextReset - now.getTime()
  const resetsInDays = Math.max(1, Math.ceil(msUntil / 86_400_000))
  return { period, resetsInDays }
}
